import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';
import { TripType } from 'contexts/searchParams';
import { OtaType } from 'detail/types';
import { IFlightsCard } from 'hooks/useFlightsCards';
import {
  IFareDetail,
  ISchedule,
  IScheduleDetail,
} from 'list/international/types';
import { Position } from './searchBox';
import {
  DateFormat,
  DurationMap,
  IItinerary,
  IKeyAnyValue,
  IKeyStringValue,
  MEMBER_FARE_CUT_LINE,
} from './types';

dayjs.locale('ko');
dayjs.extend(customParseFormat);
dayjs.extend(minMax);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

export const days = dayjs;
export type Days = Dayjs;

export const MAX_MULTI_CITY_COUNT = 6;
export const MIN_MULTI_CITY_COUNT = 2;

export function parseRoutes(routes: string[]) {
  return routes.map<IItinerary>(route => {
    const [departureAirport, arrivalAirport, departureDate] = route.split('-');
    return { departureAirport, arrivalAirport, departureDate };
  });
}

export function parseFlightsUrl(url: string) {
  const [paths, searchParams] = url.split('?');
  const [, , searchType, ...itinerary] = paths.split('/');
  if (!searchType || !itinerary.length) return null;

  return {
    searchType,
    itinerary: parseRoutes(itinerary),
    searchParams: Array.from(
      new URLSearchParams(searchParams),
    ).reduce<IKeyAnyValue>((res, [key, val]) => {
      res[key] = val;
      return res;
    }, {}),
  };
}

export const getTripInfo = (itinerary: IItinerary[]) => {
  if (!itinerary?.length) return TripType.NULL;
  if (itinerary.length === 1) return TripType.OW;
  if (
    itinerary.length === 2 &&
    itinerary[0].departureAirport === itinerary[1].arrivalAirport &&
    itinerary[0].arrivalAirport === itinerary[1].departureAirport
  ) {
    return TripType.RT;
  }
  return TripType.MD;
};

export const getDurationKr = (duration: number) => {
  const hour = Math.floor(duration / 60);
  const min = duration % 60;
  return `${hour}시간${min > 0 ? ` ${min}분` : ''}`;
};

export const hhmmRegEx = /(\d{2})(\d{2})/;

export const getTimeArray = (time: string) => {
  const [ret, hours, minutes] = hhmmRegEx.exec(time) || [null];
  return ret ? [hours || '', minutes || ''] : [];
};
export const getMinutes = (time: string | string[]) => {
  const [hours, minutes] = Array.isArray(time) ? time : getTimeArray(time);
  return +(hours ?? 0) * 60 + +(minutes ?? 0);
};

export enum AreaCode {
  EVERYWHERE = 'rcm',
  DOMESTIC = 'lsr',
  INTERNATIONAL = 'isr',
}

const getKeyOfSearchBoxInList = (tripType: TripType) => {
  switch (tripType) {
    case TripType.EVERYWHERE:
      return 'rcm';
    case TripType.OW:
      return 'lon';
    case TripType.RT:
      return 'lrd';
    case TripType.MD:
      return 'lml';
    default:
      return '';
  }
};

const getKeyOfSearchBoxInMain = (tripType: TripType) => {
  switch (tripType) {
    case TripType.EVERYWHERE:
      return 'rcm';
    case TripType.OW:
      return 'one';
    case TripType.RT:
      return 'rnd';
    case TripType.MD:
      return 'mul';
    default:
      return '';
  }
};

export function getAreaCode(position: Position, tripType: TripType) {
  if (position === Position.EVERYWHERE) {
    return 'rcm';
  }
  return (
    position === Position.LIST
      ? getKeyOfSearchBoxInList
      : getKeyOfSearchBoxInMain
  )(tripType);
}

export const getClickCodeOfList = (tripType: TripType, isDomestic: boolean) => {
  if (tripType === TripType.EVERYWHERE) {
    return AreaCode.EVERYWHERE;
  }
  return isDomestic ? AreaCode.DOMESTIC : AreaCode.INTERNATIONAL;
};

export const getClickCodeWithPrefix = (prefix: string) => (code: string) =>
  `${prefix}.${code}`;

export const getStopCount = (schedule: IScheduleDetail[]) =>
  schedule.reduce<number>((acc, { ha }) => {
    let res = acc + 1;
    ha?.forEach(({ ap, st }) => {
      if (ap && st) res += 1;
    });
    return res;
  }, 0) - 1;

export const getMaxViaCount = (scheduleDetails: ISchedule[]) =>
  Math.min(
    Math.max(...scheduleDetails.map(({ detail }) => getStopCount(detail))),
    2,
  );

export const debounce = (callback: Function, delay: number) => {
  let timeoutId: any = null;
  return (e: any, ...args: any) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(callback.bind(null, e, ...args), delay);
  };
};

const regExStayDur = /^[1-9]\d*[D|M|Y]{1}$/;
export const isStayDuration = (input: string | undefined) => {
  if (!input) return false;
  return regExStayDur.test(input);
};

export const convLegacyURL = (legacyPath: string, query: IKeyStringValue) => {
  const {
    trip,
    fareType,
    selectedFlight,
    adult,
    infant,
    child,
    stayLength,
    JrnyType,
  } = query;
  const newQueryParams = new URLSearchParams({
    adult,
    infant,
    child,
    fareType,
    selectedFlight: selectedFlight || '',
    selectType: 'concurrent',
    isDirect: JrnyType === 'NS' ? 'true' : '',
  });
  const newPathNames = [];
  const tripTypes = ['OW', 'RT', 'MD'];
  for (let i = 0; i <= tripTypes.indexOf(trip); i++) {
    const depCity = query[`scity${i + 1}`];
    const arrCity = query[`ecity${i + 1}`];
    const depDate = query[`sdate${i + 1}`] as string;
    if (depCity && arrCity) {
      const slug = `${depCity}-${arrCity}`;
      if (depDate) {
        newPathNames.push(`${slug}-${depDate.replace(/\./g, '')}`);
      } else if (stayLength) {
        const lenRegEx = /(\d+)([DMY])+/;
        const [, len, unit] = lenRegEx.exec(stayLength) || [, '0', 'D'];
        const unitSet: { [s: string]: number } = {
          D: 1,
          M: 30,
          Y: 365,
        };
        if (len && unit) {
          newPathNames.push(`${slug}-${+len * unitSet[unit]}D`);
        }
      }
    }
  }
  let newPath;
  if (legacyPath === '/flights/v2/results') {
    newPath = '/flights/international';
  } else if (legacyPath === '/flights/results/domestic') {
    newPath = '/flights/domestic';
    newQueryParams.delete('selectType');
  } else {
    return '/';
  }
  if (newQueryParams.get('selectedFlight') === '') {
    newQueryParams.delete('selectedFlight');
  }
  if (newQueryParams.get('isDirect') === '') {
    newQueryParams.delete('isDirect');
  }
  return `${newPath}/${newPathNames.join('/')}?${newQueryParams.toString()}`;
};

export function get12MonthsFromToday() {
  const thisMonth = days().startOf('month');
  return [...Array(12)].map((_, i) =>
    thisMonth.add(i, 'month').format('YYYYMM'),
  );
}

export const stringifyDepartureMonths = (departureMonths?: Set<string>) =>
  departureMonths?.size
    ? `출발 ${Array.from(departureMonths)
        .sort()
        .map(month => days(month, 'YYYYMM').format('M월'))
        .join(', ')}`
    : '출발월 전체';

export function hasTravelClubFare(fareList: IFareDetail[]) {
  // 상위 범위안에 못들면 없다고 판단
  const memberFareIdx = fareList.findIndex(
    (el: IFareDetail) => el.hideMemberFare || el.isMemberFare,
  );
  return memberFareIdx >= 0 && memberFareIdx < MEMBER_FARE_CUT_LINE;
}

export function getStayLength(itineraries: IItinerary[]) {
  const tripType = getTripInfo(itineraries);
  if (tripType === TripType.RT) {
    const [, { departureDate }] = itineraries;
    if (isStayDuration(departureDate)) return departureDate;
  }
  return '';
}

export const stringifyDepartureDate =
  (
    tripType: TripType,
    stayLength?: string,
    template: string = DateFormat.DotYMDd,
  ) =>
  ({ departureDate }: IItinerary, idx: number) =>
    tripType === TripType.RT && stayLength && idx === 1
      ? DurationMap.get(stayLength) || ''
      : days(departureDate, DateFormat.YMD).format(template);

export const modulo = (dividend: number, divisor: number) =>
  ((dividend % divisor) + divisor) % divisor;

export const getDepartureArrivalHighlight = ({
  schedule,
  index,
  isAllSelected = true,
}: {
  schedule: (IScheduleDetail[] | undefined)[];
  index: number;
  isAllSelected?: boolean;
}) => {
  if (schedule.some(ele => !ele) || schedule.length < 2)
    return { departureHighlight: false, arrivalHighlight: false };
  const typedSchedule = schedule as IScheduleDetail[][];

  const sa = typedSchedule[index][0].sa;
  const ea = typedSchedule[index][typedSchedule[index].length - 1].ea;
  const priorIndex = modulo(index - 1, typedSchedule.length);
  const subsequentIndex = modulo(index + 1, typedSchedule.length);
  const priorSchedule = typedSchedule[priorIndex];
  const subsequentSchedule = typedSchedule[subsequentIndex];
  const priorArrival = priorSchedule[priorSchedule.length - 1].ea;
  const subsequentDeparture = subsequentSchedule[0].sa;

  return {
    departureHighlight: sa !== priorArrival && (index !== 0 || isAllSelected),
    arrivalHighlight:
      ea !== subsequentDeparture &&
      (index !== schedule.length - 1 || isAllSelected),
  };
};

export const getNeedNotice = (otaId: string, otaType?: OtaType) =>
  otaType === OtaType.GLOBAL_OTA && otaId !== 'CTR013';

/**
 * 네이버페이에서 결제 수단(B Code)별로 제공하는 혜택 정보 노출을 돕는 util
 */
export const getNpayLabelInfo = (
  fareType: string,
  flightCards: IFlightsCard[],
) => {
  const bCode = (fareType || '').split('/')[1];
  const nPayPromotionAmount = flightCards.find(
    ({ naverPaySaving, code }) => code === bCode && (naverPaySaving || 0) > 0,
  )?.naverPaySaving;

  if (nPayPromotionAmount) {
    return {
      nPayPromotionAmount,
      nPayLabelPopupText: (
        <>
          네이버파이낸셜 가맹점에서
          <br />
          제공하는 혜택입니다.
          <br />
          자세한 내용은 결제 시 확인해 주세요
        </>
      ),
    };
  }

  return null;
};

function getScrollableParent(element: HTMLButtonElement) {
  let parent = element.parentElement;

  while (parent) {
    const style = getComputedStyle(parent);
    const isScrollableX =
      (style.overflowX === 'scroll' || style.overflowX === 'auto') &&
      parent.scrollWidth > parent.clientWidth;
    const isScrollableY =
      (style.overflowY === 'scroll' || style.overflowY === 'auto') &&
      parent.scrollHeight > parent.clientHeight;

    if (isScrollableX || isScrollableY) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return;
}

export function scrollXToCenter(targetElement: HTMLButtonElement) {
  const scrollContainer = getScrollableParent(targetElement);

  if (!scrollContainer) return;

  const scrollToPosition =
    targetElement.offsetLeft -
    scrollContainer.clientWidth / 2 +
    targetElement.offsetWidth / 2;

  scrollContainer.scrollTo({ left: scrollToPosition });
}
